import React from 'react';
import { ForgeTooltip } from '@tylertech/forge-react';
import { downloadProgramInsightImplementationPlan } from '../../services/downloadService.js';
import { AI_ICON_NAME, QUARTILE_DICTIONARY, TABLE_COLUMN_WIDTH } from '../../static/constants.js';
import { AI_INSIGHT_GENERATION_LABEL, AI_INSIGHTS_GENERATION_LABEL, PROGRAM, PROGRAM_DEPARTMENT, PROGRAM_DESCRIPTION, PROGRAM_DIVISION, USER_GROUP } from '../../static/keyWords.js';
import { REDUCER_ACTION_TYPES } from '../../store/Reducer.js';
import { totalCostFormat } from '../reusable/reusableFunctions.js';
import { AGGridTotalCostComparator } from '../System/AgGrid/RegisterAgGridModules.js';
import TableCellButton from '../reusable/AgGridCells/TableCellButton/TableCellButton.js';

const LABELS_AND_PLACEHOLDERS = {
  personnelCost: 'Personnel',
  nonPersonnelCost: 'NonPersonnel',
  revenueCost: 'Revenue',
};

export const INSIGHTS_PAGE_LENGTH = 10;
export const INSIGHTS_PAGINATION_PAGE_SIZE_SELECTOR_VALUES = [5, 10, 20, 50, 100];

export const getQuartileLabel = (quartileId) => QUARTILE_DICTIONARY[quartileId] || 'N/A';

export const INSIGHTS_GRAPH_OPTS = (shouldFormat = false) => {
  return {
    barPercentage: 0.5,
    categoryPercentage: 0.9,
    tooltip: {
      backgroundColor: 'black',
      borderColor: 'black',
      borderWidth: 1,
      callbacks: {
        label: (tooltip) => shouldFormat ? totalCostFormat(tooltip.raw) : tooltip.raw,
      }
    },
  };
};

export const getDefaultColumns = (showColumns, nameConventions, showRecommendButton, handleCreateInsightForProgram, handleConvertToCreateInsightPage) => {
  return [
    {
      headerName: 'Service type',
      field: 'service_type_name',
      tooltipField: 'service_type_name',
      editable: false,
      context: 'Service Type',
      hide: !showColumns?.find((column) => column.name === 'Service Type')?.show,
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      headerName: 'Quartile',
      field: 'final_quartile',
      editable: false,
      valueFormatter: (params) => getQuartileLabel(params?.value),
      tooltipValueGetter: (params) => getQuartileLabel(params?.value),
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => getQuartileLabel(params.data?.final_quartile),
      context: 'Quartile',
      hide: !showColumns?.find((column) => column.name === 'Quartile')?.show,
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      headerName: nameConventions[PROGRAM].alias,
      field: 'name',
      tooltipField: 'name',
      editable: false,
      context: 'Program',
      hide: !showColumns?.find((column) => column.name === 'Program')?.show,
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
    // uncomment if the release from team A contains Program Description inside the show columns
    // { 
    //   headerName: 'Program description', 
    //   field: 'description', 
    //   tooltipField: 'description', 
    //   editable: false,
    //   context: 'Program Description',
    //   hide: !showColumns?.find((column) => column.name === 'Program Description')?.show,
    // },
    {
      headerName: `Total ${nameConventions[PROGRAM].alias} cost`,
      field: 'totalcost',
      editable: false,
      valueFormatter: (params) => totalCostFormat(Number(params?.value)),
      tooltipValueGetter: (params) => totalCostFormat(Number(params?.value)),
      context: 'Total Cost',
      hide: !showColumns?.find((column) => column.name === 'Total Cost')?.show,
      width: TABLE_COLUMN_WIDTH.LG,
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      comparator: AGGridTotalCostComparator
    },
    {
      headerName: 'Actions',
      headerTooltip: 'Actions',
      width: TABLE_COLUMN_WIDTH.XL,
      editable: false,
      resizable: false,
      pinned: 'right',
      filter: false,
      cellStyle: {
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
      },
      cellRenderer: (params) => {
        const isExpandable = params.node.group || params.node.master;
        const isExpanded = params.node.expanded;

        return (
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', width: '100%', }}>
            {showRecommendButton &&
              <>
                <TableCellButton
                  label={AI_INSIGHT_GENERATION_LABEL}
                  data={params.data}
                  handleClick={() => handleCreateInsightForProgram(params.data)}
                  icon={AI_ICON_NAME}
                  id={`generate-ai-insight-button-${params.data.id}`}
                />
                <ForgeTooltip anchor={`generate-ai-insight-button-${params.data.id}`} placement={'bottom'}>
                  {AI_INSIGHT_GENERATION_LABEL}
                </ForgeTooltip>
              </>
            }
            <TableCellButton
              label="Create insight"
              data={params.data}
              handleClick={() => handleConvertToCreateInsightPage(params.data)}
              icon={'plus'}
              id={`create-insight-button-${params.data.id}`}
            />
            <ForgeTooltip anchor={`create-insight-button-${params.data.id}`} placement={'bottom'}>
              {'Create insight'}
            </ForgeTooltip>
            {isExpandable && (
              <>
                <TableCellButton
                  label="Toggle insight"
                  data={params.data}
                  handleClick={() => {
                    params.api.setRowNodeExpanded(params.node, !isExpanded);
                  }}
                  icon={isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  id={`toggle-insight-button-${params.node.id || params.data.id}`}
                />
                <ForgeTooltip anchor={`toggle-insight-button-${params.node.id || params.data.id}`} placement={'bottom'}>
                  {isExpanded ? 'Close' : 'Open'}
                </ForgeTooltip>
              </>
            )}
          </div>
        );
      },
      context: 'Actions',
      hide: false,
      suppressColumnsToolPanel: true,
    },
  ];
};

export const defaultExpectation = {
  type: 'Expense',
  amount: 0,
  duration: 'Ongoing',
};

export const defaultInsightData = (info, generatedInsight) => {
  const normalizeValue = (value) => value?.trim().toLowerCase();
  const programChangeType = generatedInsight
    ? (info.set.programChangeTypes || []).find(
      (changeType) => normalizeValue(changeType.name) === normalizeValue(generatedInsight.program_change_category),
    )
    : null;
  const insightBoxChoices = generatedInsight
    ? (info.set.insightBoxChoiceTypes || []).flatMap((choiceType) => {
      const generatedValue = generatedInsight[choiceType.name.trim().toLowerCase().replace(/\s+/g, '_')];
      const matchingChoice = choiceType.insightBoxChoices.find(
        (choice) => normalizeValue(choice.name) === normalizeValue(generatedValue),
      );

      return matchingChoice ? [matchingChoice] : [];
    })
    : [];

  return {
    programId: info.programInfo.id,
    insightSetId: info.set.id,
    insightBoxChoices,
    expectations: [{
      ...defaultExpectation,
      amount: generatedInsight?.impact_amount ?? defaultExpectation.amount,
    }],
    title: generatedInsight?.insight_name || '',
    description: generatedInsight?.insight_description || '',
    programChangeType,
    authorNotes: info.set.authorNoteTypes.map(noteType => ({
      description: '',
      authorNoteTypeId: noteType.id,
      isRequired: noteType.isRequired,
      question: noteType.description,
    }))
  };
};

export const userVotedOrNo = (user, insight, setCommentData,) => {
  const upvoted = insight.upvoteComments.find(comment => comment.lastUser == user.email);
  if (upvoted) {
    setCommentData && setCommentData(upvoted);
    return 'upvoted';
  }
  const downvoted = insight.downvoteComments.find(comment => comment.lastUser == user.email);
  if (downvoted) {
    setCommentData && setCommentData(downvoted);
    return 'downvoted';
  }
  const neutralVote = insight.neutralComments.find(comment => comment.lastUser == user.email);
  if (neutralVote) {
    setCommentData && setCommentData(neutralVote);
    return 'vote neutral for';
  }
  return 'did not vote for';
};

export const getVoteType = (isUserVoted) => {
  return isUserVoted === 'upvoted'
    ? 'Upvoted'
    : isUserVoted === 'downvoted'
      ? 'Downvoted'
      : 'Neutral';
};

export const truncateText = (title, size) => {
  if (title?.length > size) {
    return title.substring(0, size) + '...';
  }
  return title;
};

export const headerTabButtons = (nameConventions) => {
  const programNameConv = nameConventions.Program.pluralFormAlias;
  return [{ name: 'Insights' }, { name: programNameConv }];
};

export const headerTabButtonsDetails = (insight) => {
  const commentCount = insight?.comments?.length || 0;
  const voteCount = (insight?.upvoteComments?.length || 0) + (insight?.downvoteComments?.length || 0);

  return [
    { name: 'Details' },
    { name: 'Questions' },
    { name: 'Comments', count: commentCount },
    { name: 'Votes', count: voteCount }
  ];
};

export const headerButton = (setOpenFilters, graphDrawerIsOpen, setGraphDrawerIsOpen, activeTab, selectedInsightsCount, handleImplementationPlans) => {
  const buttons = [
    {
      id: 'filter',
      name: 'Filter',
      variant: 'text',
      icon: 'filter_list',
      onClickFunction: () => setOpenFilters(true),
      filterBadge: true,
    },
    {
      id: 'goalsPanel',
      name: 'Goals Panel',
      type: 'iconButton',
      icon: !graphDrawerIsOpen ? 'show_panel_right' : 'hide_panel_right',
      before: true,
      tooltip: !graphDrawerIsOpen ? 'Show graphic' : 'Close graphic',
      style: { margin: '0px 0px 0px 10px' },
      onClickFunction: () => setGraphDrawerIsOpen(state => !state),
    },
  ];

  if (!activeTab && selectedInsightsCount > 0) {
    buttons.unshift({
      id: 'insights',
      name: 'Get implementation plan',
      onClickFunction: handleImplementationPlans,
      useAiButton: true,
      showCondition: true
    });
  }

  return buttons;
};

export const headerButtonDetails = (setOpenDeleteDialog, user) => {
  return [
    {
      id: 'delete',
      name: 'Delete',
      variant: 'text',
      icon: 'delete',
      onClickFunction: () => setOpenDeleteDialog(true),
      disabled: user?.isViewOnly,
    },
  ];
};

export const populateComplexityAndTimeline = (insight, index, set) => {
  const type = set.insightBoxChoiceTypes[index];
  const choice = insight.insightBoxChoices.find(boxChoice => type.insightBoxChoices.find(setBoxChoice => setBoxChoice.id === boxChoice.id));
  return choice ? choice.name : '';
};

export const getBarChartInfo = (insightsData) => {
  if (!insightsData.changeTypeCount) return { dataset: [], labels: [], fullLabels: [] };
  const changeTypeCount = insightsData.changeTypeCount;
  let _labelsArr = [], _fullLabelsArr = [], _valuesArr = [];
  for (const changeType in changeTypeCount) {
    _labelsArr.push(truncateText(changeType, 15));
    _fullLabelsArr.push(changeType);
    _valuesArr.push(changeTypeCount[changeType]);
  }
  return {
    dataset: [{ data: _valuesArr, backgroundColor: ['#586ab1'] }],
    labels: _labelsArr,
    fullLabels: _fullLabelsArr,
  };
};

export const getBarChartInfoForDetails = (allocationInfo) => {
  let _labelsArr = [], _valuesArr = [];
  for (const placeholder in LABELS_AND_PLACEHOLDERS) {
    _labelsArr.push(LABELS_AND_PLACEHOLDERS[placeholder]);
    _valuesArr.push(allocationInfo[placeholder]);
  }
  return {
    dataset: [{ data: _valuesArr, backgroundColor: ['#586ab1'] }],
    labels: _labelsArr
  };
};

export const getRadarChartInfoForDetails = (programInfo, nameConventions) => {
  const resultNameConv = nameConventions.Priority.pluralFormAlias;
  let _labelsArr = [], _valuesArr = [];
  for (const info of programInfo) {
    _labelsArr.push(info.result_abbreviation);
    _valuesArr.push(info.normalizedValue);
  }
  return {
    dataset: [{ data: _valuesArr, label: `Non-scored ${resultNameConv}`, backgroundColor: 'rgb(254,177,193,0.5)', borderColor: 'rgb(253,161,181)' }],
    labels: _labelsArr
  };
};

export const getResultFinalScore = (result) => {
  let finalScore = result.scores.find((score) => score.scoreTypeId === 3);
  if (finalScore) {
    let finalScoreLabel = result.scoreOptions.find((scoreOption) => scoreOption.id === finalScore.scoreOptionId);
    return finalScoreLabel ? finalScoreLabel.name : 'N/A';
  }
  return 'N/A';
};

export const applyChangesAfterEdit = (data, setInsight) => {
  data.type ? setInsight(prevInsight => {
    return {
      ...prevInsight,
      comments: [...prevInsight.comments.filter(comment => comment.id != data.id), data],
      upvoteComments: data.type === 'Upvoted' ? [...prevInsight.upvoteComments.filter(vote => vote.id != data.id), data] : prevInsight.upvoteComments.filter(vote => vote.id != data.id),
      downvoteComments: data.type === 'Downvoted' ? [...prevInsight.downvoteComments.filter(vote => vote.id != data.id), data] : prevInsight.downvoteComments.filter(vote => vote.id != data.id)
    };
  }) : setInsight(prevInsight => {
    return {
      ...prevInsight,
      comments: prevInsight.comments.filter(comment => comment.id != data.id),
      upvoteComments: prevInsight.upvoteComments.filter(vote => vote.id != data.id),
      downvoteComments: prevInsight.downvoteComments.filter(vote => vote.id != data.id)
    };
  });
};

export const mapInsightForSave = (insight) => {
  const currentBudgetImpact = insight?.budgetImpact;
  const budgetImpact = currentBudgetImpact?.trim().length ? currentBudgetImpact?.trim() : '';
  const title = insight?.title?.trim();
  const description = insight.description?.trim();

  const expectationsToSave = insight.expectations.map(expectation => {
    if (!expectation.insightId) {
      const { id, ...updatedExpectation } = expectation;
      return updatedExpectation;
    }
    return expectation;
  });

  return {
    ...insight,
    title,
    description,
    budgetImpact,
    expectations: expectationsToSave
  };
};

export const getTitle = (insightData, infoForCreateInsightPage, messageManager, nameConventions) => {
  return insightData
    ? messageManager(nameConventions).PROGRAM_INSIGHTS.DETAILS_TITLE
    : infoForCreateInsightPage
      ? messageManager(nameConventions).PROGRAM_INSIGHTS.CREATE_TITLE
      : messageManager(nameConventions).PROGRAM_INSIGHTS.MAIN_TITLE;
};

export const getHeaderButtons = (
  infoForCreateInsightPage,
  insightData,
  editDetailsTab,
  setOpenDeleteDialog,
  setOpenFilters,
  user,
  graphDrawerIsOpen,
  setGraphDrawerIsOpen,
  activeTab,
  selectedCount,
  handleImplementationPlans
) => {
  return infoForCreateInsightPage
    ? []
    : insightData
      ? !editDetailsTab
        ? headerButtonDetails(setOpenDeleteDialog, user)
        : []
      : headerButton(setOpenFilters, graphDrawerIsOpen, setGraphDrawerIsOpen, activeTab, selectedCount, handleImplementationPlans);
};

export const getHeaderTabButtons = (editDetailsTab, infoForCreateInsightPage, insightData, nameConventions) => {
  return editDetailsTab || infoForCreateInsightPage
    ? []
    : insightData
      ? headerTabButtonsDetails(insightData)
      : headerTabButtons(nameConventions);
};

export const handleGoBackHelper = (editDetailsTab, infoForCreateInsightPage, state, dispatch, setEditDetailsTab, setInfoForCreateInsightPage, handleGoBack) => {
  return editDetailsTab || infoForCreateInsightPage
    ? state.changesWereMade
      ? () => dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated })
      : () => { setEditDetailsTab(false); setInfoForCreateInsightPage(null); dispatch({ type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED, payload: false }); }
    : handleGoBack;
};

export const getButtonVariant = (isUserVoted, type) => {
  return isUserVoted == type ? 'tonal' : 'text';
};

export const getButtonStyle = (isUserVoted, type,) => {
  return isUserVoted == type ? { border: `1px solid var(--forge-theme-${type === 'upvoted' ? 'success' : 'error'})` } : {};
};

export const isDataCorrect = (_insight) => {
  return _insight.title.trim().length > 0 &&
    _insight.title.trim().length <= 100 &&
    _insight.description.trim().length > 0 &&
    !!_insight.programChangeType?.name &&
    !_insight.expectations.find(exp => exp.amount === '');
};

export const removeCommentFromInsight = (insight, commentId) => {
  let _insight = JSON.parse(JSON.stringify(insight));
  _insight.comments = insight.comments.filter(comment => comment.id !== commentId);
  _insight.upvoteComments = insight.upvoteComments.filter(comment => comment.id !== commentId);
  _insight.downvoteComments = insight.downvoteComments.filter(comment => comment.id !== commentId);
  _insight.myComment = insight.myComment.id === commentId ? null : insight.myComment;
  return _insight;
};

export const areExpectationsEqual = (currentExp, originalExp) => {
  if (currentExp.length !== originalExp.length) return false;

  const prepare = (list) =>
    list
      .map(e => ({
        type: e.type,
        amount: Number(e.amount),
        duration: e.duration
      }))
      .sort((a, b) =>
        a.type.localeCompare(b.type) ||
        a.amount - b.amount ||
        a.duration.localeCompare(b.duration)
      );

  return JSON.stringify(prepare(currentExp)) === JSON.stringify(prepare(originalExp));
};


export const areChoicesEqual = (currentChoices, originalChoices) => {
  if (currentChoices.length !== originalChoices.length) return false;

  const prepare = (list) =>
    list
      .map(c => ({
        boxChoiceTypeId: c.boxChoiceTypeId,
        choiceId: c.id
      }))
      .sort((a, b) => a.boxChoiceTypeId - b.boxChoiceTypeId);

  return JSON.stringify(prepare(currentChoices)) === JSON.stringify(prepare(originalChoices));
};

export const getProgramColumns = (nameConventions) => {
  const programNameHeader = nameConventions[PROGRAM].alias || PROGRAM;
  const programDescriptionHeader = nameConventions[PROGRAM] ? `${nameConventions[PROGRAM].alias} description` : PROGRAM_DESCRIPTION;
  const programDivisionHeader = nameConventions[PROGRAM_DIVISION].alias || PROGRAM_DIVISION;
  const userGroupHeader = nameConventions[USER_GROUP].alias || USER_GROUP;
  const programDepartmentHeader = nameConventions[PROGRAM_DEPARTMENT].alias || PROGRAM_DEPARTMENT;

  return [
    {
      colId: '1',
      headerName: programNameHeader,
      headerTooltip: programNameHeader,
      tooltipField: 'programName',
      field: 'programName',
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
    {
      colId: '2',
      headerName: 'Service type',
      headerTooltip: 'Service type',
      tooltipField: 'serviceType',
      field: 'serviceType',
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      colId: '3',
      headerName: programDescriptionHeader,
      headerTooltip: programDescriptionHeader,
      tooltipField: 'description',
      field: 'description',
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
    {
      colId: '4',
      headerName: programDepartmentHeader,
      headerTooltip: programDepartmentHeader,
      tooltipField: 'department',
      field: 'department',
      width: TABLE_COLUMN_WIDTH.XL
    },
    {
      colId: '5',
      headerName: programDivisionHeader,
      headerTooltip: programDivisionHeader,
      tooltipField: 'division',
      field: 'division',
      width: TABLE_COLUMN_WIDTH.XL
    },
    {
      colId: '6',
      headerName: userGroupHeader,
      headerTooltip: userGroupHeader,
      tooltipField: 'costCenter',
      field: 'costCenter',
      width: TABLE_COLUMN_WIDTH.XL
    },
    {
      colId: '7',
      headerName: 'Total cost',
      headerTooltip: 'TotalCost',
      tooltipField: 'totalProgramCost',
      field: 'totalProgramCost',
      valueFormatter: (params) => totalCostFormat(Number(params?.value)),
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
  ];
};

export const getPredictedInsightsColumns = (nameConventions, predictionSet) => {
  const [firstChoice, secondChoice] = predictionSet?.insightBoxChoiceTypes || [];

  const programAlias = nameConventions?.[PROGRAM]?.alias || PROGRAM;
  const programChangeTypes = predictionSet?.programChangeTypes || [];

  return [
    {
      colId: '1',
      headerName: programAlias,
      headerTooltip: programAlias,
      tooltipField: 'program_name',
      field: 'program_name',
      width: TABLE_COLUMN_WIDTH.LG,
      editable: false,
    },
    {
      colId: '2',
      headerName: 'Insight set',
      headerTooltip: 'Insight set',
      valueGetter: () => predictionSet?.name || 'N/A',
      tooltipValueGetter: () => predictionSet?.name || 'N/A',
      width: TABLE_COLUMN_WIDTH.LG,
      editable: false,
    },
    {
      colId: '3',
      headerName: 'Program change category',
      headerTooltip: 'Program change category',
      tooltipField: 'program_change_category',
      field: 'program_change_category',
      width: TABLE_COLUMN_WIDTH.LG,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: programChangeTypes.map(changeType => changeType.name),
        allowTyping: true,
        highlightMatch: true,
      },
    },
    {
      colId: '4',
      headerName: 'Insight title',
      headerTooltip: 'Insight title',
      tooltipField: 'insight_name',
      field: 'insight_name',
      width: TABLE_COLUMN_WIDTH.MD,
    },
    {
      colId: '5',
      headerName: 'Insight description',
      headerTooltip: 'Insight description',
      tooltipField: 'insight_description',
      field: 'insight_description',
      width: TABLE_COLUMN_WIDTH.DEFAULT,
    },
    {
      colId: '6',
      headerName: firstChoice?.name || '',
      headerTooltip: firstChoice?.name || '',
      tooltipField: firstChoice?.name.toLowerCase() || '',
      field: firstChoice?.name.toLowerCase() || '',
      width: TABLE_COLUMN_WIDTH.XS,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: firstChoice?.insightBoxChoices?.map(choice => choice.name) || [],
        allowTyping: true,
        highlightMatch: true,
      },
    },
    {
      colId: '7',
      headerName: secondChoice?.name || '',
      headerTooltip: secondChoice?.name || '',
      tooltipField: secondChoice?.name.toLowerCase() || '',
      field: secondChoice?.name.toLowerCase() || '',
      width: TABLE_COLUMN_WIDTH.XS,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: secondChoice?.insightBoxChoices?.map(choice => choice.name) || [],
        allowTyping: true,
        highlightMatch: true,
      },
    },
  ];
};

export const getInsightGeneratorSelectionToolbarButtons = (createInsights, showSaveButton) => {
  if (!showSaveButton) return [];

  return [
    {
      id: 'save',
      type: 'selection',
      name: 'Save',
      icon: 'save',
      variant: 'text',
      onClickFunction: () => createInsights(),
      showCondition: true,
      disabled: false,
    },
  ];
};


/**
 * Transforms an array of raw insights into the structured DTO array for the backend.
 */
export const createInsightsArray = (gridRef, currentSet) => {
  const rawInsights = [];
  gridRef.current.api.forEachNode(node => {
    if (node.isSelected()) {
      rawInsights.push(node.data);
    }
  });

  if (!rawInsights || rawInsights.length === 0) return [];

  return rawInsights.map(rawInsight => {
    const matchedChangeType = currentSet.programChangeTypes.find(cat => {
      return cat.name === rawInsight.program_change_category;
    });

    const insightBoxChoices = [];

    // 2. Map Timeline Choice
    if (currentSet.insightBoxChoiceTypes) {
      currentSet.insightBoxChoiceTypes.forEach(choiceType => {
        // Get the name of the type (e.g., "Duration", "Timeline", "Complexity")
        const typeNameAttr = choiceType.name.toLowerCase().trim();

        // See if the rawInsight object has a key that matches this type name
        // This dynamically checks rawInsight["duration"], rawInsight["timeline"], etc.
        const rawValue = rawInsight[typeNameAttr];

        if (rawValue) {
          const normalizedValue = rawValue.toLowerCase().trim();

          // Find the choice inside this group that matches the value (e.g., "long")
          const matchedChoice = choiceType.insightBoxChoices.find(choice =>
            choice.name.toLowerCase().includes(normalizedValue)
          );

          if (matchedChoice) {
            insightBoxChoices.push(matchedChoice);
          }
        }
      });
    }

    // 4. Map Author Notes from templates
    const authorNotes = currentSet.authorNoteTypes.map(noteType => ({
      description: '',
      authorNoteTypeId: noteType.id,
      isRequired: noteType.isRequired,
      question: noteType.description
    }));

    // Return the individual mapped object into the new array
    return {
      programId: rawInsight.program_id,
      insightBoxChoices,
      expectations: [
        {
          type: 'Expense',
          amount: 0,
          duration: 'Ongoing'
        }
      ],
      title: rawInsight.insight_name,
      description: rawInsight.insight_description,
      authorNotes,
      programChangeType: matchedChangeType
    };
  });
};

export const triggerChangeIntent = (state, dispatch) => {
  if (state.fireUnsavedChangesDialog) return;
  dispatch({
    type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
    payload: true,
  });
};

export const singleProgramBody = (prog) => {
  return {
    id: prog.programId,
    program_name: prog.programName,
    description: prog.description,
    department: prog.department,
    total_cost: prog.totalProgramCost,
    personnel: prog.personnelCost,
    non_personnel: prog.nonPersonnelCost,
    revenue: prog.totalRevenue,
    fte: prog.fteCount,
  };
};

export const getInsightPredictorSplitButtonOptions = (implementationPlans, user) => [
  {
    label: 'As Excel',
    value: 'excel',
    icon: 'file_excel',
    leadingIconType: 'component',
    leadingIconComponentProps: { external: true },
    onClick: () => downloadProgramInsightImplementationPlan(implementationPlans, 'xlsx', user)
  },
  {
    label: 'As PDF',
    value: 'pdf',
    icon: 'file_pdf',
    leadingIconType: 'component',
    leadingIconComponentProps: { external: true },
    onClick: () => downloadProgramInsightImplementationPlan(implementationPlans, 'pdf', user)
  }
];

export const getRowSelectionMode = (showRecommendButton) => {
  if (!showRecommendButton) return undefined;
  return {
    mode: 'multiRow',
    selectAll: 'currentPage',
    isRowSelectable: (params) => params.data && !params.data.Error
  };
};

export const mapInsightsBody = (_selectedInsights, user) => {

  const mappedInsights = _selectedInsights.map(insight => {

    const timeline = insight?.insightBoxChoices?.find(choice => choice.boxChoiceTypeId === 6)?.name || '';
    const [department,] = insight?.program?.organizationLevels ? insight.program.organizationLevels.split('--') : '';

    return {
      complexity: insight?.insightBoxChoices[0]?.name || '',
      impact_amount: insight?.expectations[0]?.amount || 0,
      implementing_organization: user?.activeOrganization,
      insight: [],
      insight_description: insight?.description || '',
      insight_name: insight?.title || '',
      program_change_category: insight?.programChangeType?.name || '',
      program_id: insight?.programId || null,
      program_name: insight?.program?.name || '',
      timeline,
      department,
    };
  });

  return mappedInsights;
};

export const getSelectionToolBarButtons = (handleGenerateInsights) => {
  return [
    {
      id: 'get_ai_insights',
      name: AI_INSIGHTS_GENERATION_LABEL,
      useAiButton: true,
      showCondition: true,
      type: 'selection',
      onClickFunction: () => handleGenerateInsights()
    },
  ];
};
